import React, { useEffect, useMemo, useState } from 'react';
import { ToggleSwitch } from 'components/common';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { FiSave, FiX, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { createUser, updateUser } from 'redux/authorization/userSlice';
import { getActiveSalesUnits } from 'redux/crm/salesUnitSlice';
import { debounce, filterOptions } from 'helpers/debounce';
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '36px',
    fontSize: '13px',
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(59,130,246,0.2)' : 'none',
    '&:hover': { borderColor: '#3b82f6' }
  }),
  placeholder: (base) => ({ ...base, fontSize: '13px', color: '#9ca3af' }),
  input:       (base) => ({ ...base, fontSize: '13px' }),
  singleValue: (base) => ({ ...base, fontSize: '13px' }),
  option:      (base, state) => ({
    ...base,
    fontSize: '13px',
    backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
    color: state.isSelected ? 'white' : '#1f2937'
  }),
  menu:  (base) => ({ ...base, zIndex: 9999 }),
  // Portal the menu above the modal (taiwincss Modal zIndex is 100000) so it
  // isn't clipped/overlapped by the modal body. Used with menuPortalTarget.
  menuPortal: (base) => ({ ...base, zIndex: 100001 }),
};

const selectPortalProps = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed',
};

const FieldRow = ({ label, required, error, children }) => (
  <div className="tw-mb-4">
    <label className="tw-block tw-text-xs tw-font-semibold tw-text-gray-600 tw-mb-1">
      {label}
      {required && <span className="tw-text-red-500 tw-ml-0.5">*</span>}
    </label>
    {children}
    {error && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{error}</p>}
  </div>
);

const SwitchRow = ({ label, checked, onChange }) => (
  <div className="tw-flex tw-items-center tw-justify-between tw-py-2">
    <span className="tw-text-sm tw-text-gray-700">{label}</span>
    <ToggleSwitch value={!!checked} onChange={onChange} />
  </div>
);

SwitchRow.propTypes = {
  label: PropTypes.node,
  checked: PropTypes.bool,
  onChange: PropTypes.func
};


const UserForm = ({ title, user, isModal, onSuccess, onCancel }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue, getValues } = useForm();
  const { activeSalesUnitsData: salesUnits } = useSelector((state) => state.crm_sales_unit);
  const [salesUnitOptions, setSalesUnitOptions] = useState([]);
  const [isSubmitting, setIsSubmitting]         = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getActiveSalesUnits());
  }, [dispatch]);

  useEffect(() => {
    setValue('time_zone', Intl.DateTimeFormat().resolvedOptions().timeZone);
    if (user) {
      Object.entries(user).forEach(([key, value]) => {
        if (key === 'site_id') loadSalesUnitBySite(value);
        setValue(key, value);
      });
    }
  // seed the form once on mount; later edits belong to the user, not a re-seed from props
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue]);

  const defaultSiteOption = useMemo(() => (
    user ? { value: user.site_id, label: user.site?.name } : null
  ), [user]);

  const debouncedFilterSites = debounce((inputValue, callback) => {
    filterOptions('/organization/sites/active_list', inputValue).then(callback);
  }, 300);

  const siteOptions = (inputValue, callback) => debouncedFilterSites(inputValue, callback);

  const loadSalesUnitBySite = (site_id) => {
    const opts = (salesUnits || [])
      .filter(item => item.site_id === site_id)
      .map(opt => ({ value: opt.id, label: `${opt.name} - ${opt.description}` }));
    setSalesUnitOptions(opts);
  };

  const handleSiteChange = (selectedOption) => {
    setValue('sales_unit_id', null);
    const value = selectedOption?.value;
    loadSalesUnitBySite(value);
    setValue('site_id', value);
  };

  const validatePasswordConfirmation = (value) => {
    const { password } = getValues();
    return password === value || 'Password confirmation does not match.';
  };

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      let userId = user ? user.id : null;

      if (!user) {
        const response = await dispatch(createUser(data)).unwrap();
        userId = response.data.id;
        toast.success('User created successfully');
      } else {
        await dispatch(updateUser({ userId, data })).unwrap();
        toast.success('User updated successfully');
      }

      if (onSuccess) {
        onSuccess(userId);
      } else if (userId) {
        navigate(`/authorization/users/${userId}`);
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to save user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    else navigate(-1);
  };

  const formContent = (
    <form onSubmit={handleSubmit(onSubmit)} className="tw-px-6 tw-py-5">
      {/* Basic info */}
      <div className="tw-mb-5">
        <h3 className="tw-text-xs tw-font-bold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-3">
          Account Info
        </h3>

        <FieldRow label="Code" required error={errors.name?.message}>
          <input
            type="text"
            placeholder="3-character code (e.g. THV)"
            className={`tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-rounded tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 ${
              errors.name ? 'tw-border-red-400' : 'tw-border-gray-300'
            }`}
            {...register('name', { required: 'Code is required.' })}
          />
        </FieldRow>

        <FieldRow label="Full Name" required error={errors.full_name?.message}>
          <input
            type="text"
            placeholder="Full name"
            className={`tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-rounded tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 ${
              errors.full_name ? 'tw-border-red-400' : 'tw-border-gray-300'
            }`}
            {...register('full_name', { required: 'Full name is required.' })}
          />
        </FieldRow>

        <FieldRow label="Email" required error={errors.email?.message}>
          <input
            type="email"
            placeholder="example@pebsteel.com.vn"
            autoComplete="username"
            className={`tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-rounded tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 ${
              errors.email ? 'tw-border-red-400' : 'tw-border-gray-300'
            }`}
            {...register('email', {
              required: 'Email is required.',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                message: 'Invalid email address.'
              }
            })}
          />
        </FieldRow>
      </div>

      {/* Password (new users only) */}
      {!user && (
        <div className="tw-mb-5">
          <h3 className="tw-text-xs tw-font-bold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-3">
            Password
          </h3>

          <FieldRow label="Password" required error={errors.password?.message}>
            <input
              type="password"
              placeholder="Min 6 characters"
              autoComplete="new-password"
              className={`tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-rounded tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 ${
                errors.password ? 'tw-border-red-400' : 'tw-border-gray-300'
              }`}
              {...register('password', {
                required: 'Password is required.',
                minLength: { value: 6, message: 'Password must be at least 6 characters.' }
              })}
            />
          </FieldRow>

          <FieldRow label="Confirm Password" required error={errors.password_confirmation?.message}>
            <input
              type="password"
              placeholder="Repeat password"
              autoComplete="new-password"
              className={`tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-rounded tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 ${
                errors.password_confirmation ? 'tw-border-red-400' : 'tw-border-gray-300'
              }`}
              {...register('password_confirmation', {
                required: 'Password confirmation is required.',
                validate: validatePasswordConfirmation
              })}
            />
          </FieldRow>
        </div>
      )}

      {/* Permissions */}
      <div className="tw-mb-5">
        <h3 className="tw-text-xs tw-font-bold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-2">
          Permissions
        </h3>
        <div className="tw-border tw-border-gray-200 tw-rounded tw-divide-y tw-divide-gray-100 tw-px-3">
          <Controller
            control={control}
            name="active"
            defaultValue={true}
            render={({ field }) => (
              <SwitchRow label="Active" checked={!!field.value} onChange={(v) => setValue('active', v)} />
            )}
          />
          <Controller
            control={control}
            name="admin_meeting_room"
            defaultValue={false}
            render={({ field }) => (
              <SwitchRow label="Admin Meeting Room" checked={!!field.value} onChange={(v) => field.onChange(v)} />
            )}
          />
          <Controller
            control={control}
            name="all_sales_units"
            defaultValue={false}
            render={({ field }) => (
              <SwitchRow label="View all sales units on dashboard" checked={!!field.value} onChange={(v) => field.onChange(v)} />
            )}
          />
        </div>
      </div>

      {/* Location */}
      <div className="tw-mb-5">
        <h3 className="tw-text-xs tw-font-bold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-3">
          Location
        </h3>

        <FieldRow label="Site" required error={errors.site_id?.message}>
          <Controller
            control={control}
            name="site_id"
            rules={{ required: 'Site is required.' }}
            render={() => (
              <AsyncSelect
                cacheOptions
                defaultOptions
                defaultValue={defaultSiteOption}
                loadOptions={siteOptions}
                closeMenuOnSelect
                placeholder="Select site…"
                classNamePrefix="react-select"
                onChange={handleSiteChange}
                styles={selectStyles}
                {...selectPortalProps}
              />
            )}
          />
        </FieldRow>

        <FieldRow label="Sales Unit">
          <Controller
            control={control}
            name="sales_unit_id"
            render={({ field }) => (
              <Select
                {...field}
                closeMenuOnSelect
                options={salesUnitOptions}
                value={salesUnitOptions.find(o => o.value === field.value) || null}
                placeholder="Select sales unit…"
                classNamePrefix="react-select"
                isClearable
                onChange={(opt) => setValue('sales_unit_id', opt ? opt.value : null)}
                styles={selectStyles}
                {...selectPortalProps}
              />
            )}
          />
        </FieldRow>

        <FieldRow label="Time Zone">
          <input
            type="text"
            disabled
            className="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-200 tw-rounded tw-bg-gray-50 tw-text-gray-500 tw-cursor-not-allowed"
            {...register('time_zone')}
          />
        </FieldRow>
      </div>

      {/* Footer buttons */}
      <div className={`tw-flex tw-justify-end tw-gap-2 tw-pt-4 tw-border-t tw-border-gray-100 ${isModal ? '' : 'tw-pb-2'}`}>
        <button
          type="button"
          onClick={handleCancel}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded hover:tw-bg-gray-50 tw-transition-colors"
        >
          {isModal ? <FiX size={13} /> : <FiArrowLeft size={13} />}
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
        >
          {isSubmitting ? (
            <span className="tw-w-4 tw-h-4 tw-border-2 tw-border-white/30 tw-border-t-white tw-rounded-xl tw-animate-spin" />
          ) : (
            <FiSave size={13} />
          )}
          {isSubmitting ? 'Saving…' : 'Save User'}
        </button>
      </div>
    </form>
  );

  if (isModal) return formContent;

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
      <div className="tw-flex tw-justify-between tw-items-center tw-px-6 tw-py-4 tw-border-b tw-border-gray-200">
        <h2 className="tw-text-base tw-font-bold tw-text-gray-900">{title || (user ? 'Edit User' : 'New User')}</h2>
        <button
          onClick={handleCancel}
          className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded tw-border tw-border-gray-200 tw-bg-white hover:tw-bg-gray-50 tw-transition-colors"
        >
          <FiArrowLeft size={15} className="tw-text-gray-600" />
        </button>
      </div>
      {formContent}
    </div>
  );
};

FieldRow.propTypes = {
  label:    PropTypes.string.isRequired,
  required: PropTypes.bool,
  error:    PropTypes.string,
  children: PropTypes.node.isRequired
};
FieldRow.defaultProps = { required: false, error: null };


UserForm.propTypes = {
  title:     PropTypes.string,
  user:      PropTypes.object,
  isModal:   PropTypes.bool,
  onSuccess: PropTypes.func,
  onCancel:  PropTypes.func
};

UserForm.defaultProps = {
  title:     null,
  user:      null,
  isModal:   false,
  onSuccess: null,
  onCancel:  null
};

export default UserForm;
